import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Plus, Info } from 'lucide-react';
import { toast } from 'sonner';
import { linkedinPostsAPI } from '../../services/api';
import { LinkedInPostForm } from './LinkedInPostForm';
import { LinkedInPostCard } from './LinkedInPostCard';

const EMPTY_FORM = { title: '', embedCode: '', linkedinUrl: '', order: 0 };

const Instructions = () => (
  <Card className="border-blue-200 bg-blue-50">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
        <Info className="w-5 h-5" /> How to Get LinkedIn Embed Code
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm text-blue-700 space-y-2">
      <ol className="list-decimal list-inside space-y-1">
        <li>Go to your LinkedIn post</li>
        <li>Click the three dots (...) in the top-right corner of the post</li>
        <li>Select <strong>"Embed this post"</strong></li>
        <li>Copy the entire iframe code that appears</li>
        <li>Paste it below in the "Embed Code" field</li>
      </ol>
      <p className="mt-2 text-xs text-blue-600">Note: Only public posts can be embedded.</p>
    </CardContent>
  </Card>
);

const LinkedInPostsManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const loadPosts = useCallback(async () => {
    try {
      const res = await linkedinPostsAPI.getAll();
      setPosts(res.data || []);
    } catch {
      toast.error('Failed to load LinkedIn posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, order: posts.length });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.embedCode.trim()) return toast.error('Please enter the embed code');
    try {
      if (editingId) {
        await linkedinPostsAPI.update(editingId, formData);
        toast.success('LinkedIn post updated');
      } else {
        await linkedinPostsAPI.create({ ...formData, order: posts.length });
        toast.success('LinkedIn post added');
      }
      resetForm();
      loadPosts();
    } catch {
      toast.error('Failed to save LinkedIn post');
    }
  };

  const handleEdit = (post) => {
    setFormData({ title: post.title || '', embedCode: post.embedCode || '', linkedinUrl: post.linkedinUrl || '', order: post.order || 0 });
    setEditingId(post._id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await linkedinPostsAPI.delete(id);
      toast.success('LinkedIn post deleted');
      loadPosts();
    } catch {
      toast.error('Failed to delete post');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="max-w-4xl space-y-6">
      <Instructions />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>LinkedIn Posts</CardTitle>
            <CardDescription>{posts.length} posts embedded</CardDescription>
          </div>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} data-testid="add-linkedin-post-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Post
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <LinkedInPostForm formData={formData} setFormData={setFormData} editing={!!editingId} onSubmit={handleSubmit} onCancel={resetForm} />
          )}
          <div className="space-y-4">
            {posts.map((post, index) => (
              <LinkedInPostCard key={post._id} post={post} index={index} onEdit={() => handleEdit(post)} onDelete={() => handleDelete(post._id)} />
            ))}
            {posts.length === 0 && !showAddForm && (
              <div className="text-center py-12 text-gray-500">
                <p className="mb-4">No LinkedIn posts added yet.</p>
                <Button onClick={() => setShowAddForm(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Post
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LinkedInPostsManager;
